import React, { useEffect, useState } from "react";
import { useDeviceTypes } from "halifax";
import { RouteComponentProps } from "react-router-dom";
import { B2B_PORTAL_BANNER_CONTAINER_ID, RewardsAccount } from "redmond";

import "./styles.scss";
import clsx from "clsx";
import { Box, Slide, useScrollTrigger } from "@material-ui/core";
import { useIsTravelApp, useUserContext } from "@capone/common";
import {
  HIDDEN_BANNER_UNPROTECTED_PATHS,
  HIDDEN_HEADER_PATHS_DESKTOP,
  isCoreHomepageBookingConfirmationPath,
  pathsListContainsPath,
  PREMIER_COLLECTION_BANNER_STYLE_PATHS,
  PRICE_MATCH_BANNER_PATHS,
} from "../../utils/urlPaths";
import { isPremiumStaysFunnelPath } from "../../utils/homepageEntryPaths";
import { CapOneHeaderHomePageRightSection } from "../Header/components/DesktopHeader";
import {
  getExperimentVariantCustomVariants,
  getExperimentVariant,
  LC_GATING_EXPERIMENT,
  AVAILABLE,
  CONTROL,
  useExperiments,
  CORE_HOMEPAGE,
  CUSTOMER_PROFILE_EXPERIMENT,
  CUSTOMER_PROFILE_VARIANTS,
  PREMIER_COLLECTION_EXPERIMENT,
  XMOD_V1_EXPERIMENT,
  CORE_HOTELS,
} from "../../context/experiments";
import fetchUserPreferencesStatus, {
  UserPreferencesStatusPayload,
} from "../../api/v1/user/fetchUserPreferencesStatus";

export interface IRootBannerProps extends RouteComponentProps {
  headerDisplayed?: boolean;
  isHidden?: boolean;
  canViewConsumer?: boolean;
  hideOnScroll?: boolean;
  rewardsAccounts: RewardsAccount[];
  isRewardsDisabled?: boolean;
}

const HideOnScroll = ({ children }: { children: JSX.Element }) => {
  const trigger = useScrollTrigger({ disableHysteresis: true });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
};

export const RootBanner = (props: IRootBannerProps) => {
  const {
    headerDisplayed,
    isHidden,
    rewardsAccounts,
    isRewardsDisabled,
    ...browserRouterProps
  } = props;
  const { history } = browserRouterProps;
  const { matchesMobile } = useDeviceTypes();
  const [renderRightNav, setRenderRightNav] = useState(true);
  const [userPreferencesStatus, setUserPreferencesStatus] =
    useState<UserPreferencesStatusPayload>();

  const { isBusinessEligible } = useUserContext("capone");
  const expState = useExperiments();

  const { isTravelApp } = useIsTravelApp();

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleOnlyCollectionEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
      AVAILABLE && !isPremierCollectionEnabled;

  const isCustomerProfileExperiment =
    getExperimentVariantCustomVariants(
      expState.experiments,
      CUSTOMER_PROFILE_EXPERIMENT,
      CUSTOMER_PROFILE_VARIANTS
    ) !== CONTROL;

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isCoreHotelsEnabled =
    getExperimentVariant(expState.experiments, CORE_HOTELS) === AVAILABLE;
  const isCoreHomepageEnabled = getExperimentVariant(
    expState.experiments,
    CORE_HOMEPAGE
  ).startsWith(AVAILABLE);

  useEffect(() => {
    const fetchPreferencesStatus = async () => {
      const prefsStatus = await fetchUserPreferencesStatus();
      setUserPreferencesStatus(prefsStatus);
    };
    if (isCustomerProfileExperiment) {
      fetchPreferencesStatus();
    }
  }, [isCustomerProfileExperiment]);

  useEffect(() => {
    if (!matchesMobile) {
      const pathname = history.location.pathname;
      const isInHiddenPaths = pathsListContainsPath(
        HIDDEN_HEADER_PATHS_DESKTOP,
        pathname
      );

      const isConfirmationPage = pathname.includes("/confirm/");
      const isOnHotelsFunnel =
        isCoreHotelsEnabled &&
        /^\/hotels\/[^/]+/.test(pathname) &&
        !isConfirmationPage;
      const isOnStaysFunnel =
        isCoreHotelsEnabled &&
        /^\/stays\/[^/]+/.test(pathname) &&
        !isConfirmationPage;
      const isOnPremiumStaysFunnel = isPremiumStaysFunnelPath(pathname);
      const isCoreHomepageBookingConfirmation =
        isCoreHomepageEnabled &&
        isCoreHomepageBookingConfirmationPath(pathname);

      if (
        isInHiddenPaths ||
        isHidden ||
        isOnHotelsFunnel ||
        isOnStaysFunnel ||
        isOnPremiumStaysFunnel ||
        isCoreHomepageBookingConfirmation
      ) {
        setRenderRightNav(false);
      } else {
        setRenderRightNav(true);
      }
    }
  }, [
    history.location.pathname,
    matchesMobile,
    isHidden,
    isCoreHomepageEnabled,
    isCoreHotelsEnabled,
  ]);

  if (isTravelApp) return null;

  const content = (
    <Box
      className={clsx("b2b-portal-banner-root", {
        "includes-right-section":
          renderRightNav && !matchesMobile && !isBusinessEligible,
        mobile: matchesMobile,
        "checkout-banner": PRICE_MATCH_BANNER_PATHS.includes(
          history.location.pathname
        ),
        isHidden,
        "pc-banner":
          PREMIER_COLLECTION_BANNER_STYLE_PATHS.includes(
            history.location.pathname
          ) && !isLifestyleOnlyCollectionEnabled,
        "includes-right-section-corporate":
          renderRightNav && !matchesMobile && isBusinessEligible,
        xmod: isXmodRedesign,
        "rewards-disabled": isRewardsDisabled,
      })}
    >
      <div
        id={B2B_PORTAL_BANNER_CONTAINER_ID}
        className={clsx({
          mobile: matchesMobile,
          isHidden: HIDDEN_BANNER_UNPROTECTED_PATHS.includes(
            history.location.pathname
          ),
        })}
      />

      {renderRightNav && !matchesMobile && (
        <CapOneHeaderHomePageRightSection
          showCustomerProfileNewBadge={
            userPreferencesStatus?.hasUsedPreferences === false
          }
          displayOnlyIcon
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      )}
    </Box>
  );

  return props.hideOnScroll ? <HideOnScroll>{content}</HideOnScroll> : content;
};
